import { useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { NEW_TAB } from "../shared/embed";
import { POI_DOT, POI_FILL } from "../shared/mapModel";
import { MarkerIcon } from "../shared/markers";
import { COUNTRIES, HOSTEL_SLUGS, HOSTELS, nightsOf, tripsVia, TRIPS, type Hostel, type Poi } from "./content";
import { appUrl, HOME_PATH, hostelPath, mmUrl, routePreviewHref, startPath } from "./links";
import LiveMap from "./LiveMap";
import { hostelPois } from "./livePois";
import { fmtKm, hostelMarker, hostelPoint, POI_GROUP_LABEL, poiMarkers, type PoiMarkerData } from "./mapModel";
import { PoiCard } from "./PlaceCard";
import { defNights, km, knownLeg } from "./plannerLogic";
import { setRoute } from "./routeStore";
import { Arrow, Flag } from "./ui";
import styles from "./HostelPage.module.scss";
import shared from "./MadMonkey.module.scss";

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(" ");

const GROUPS = ["do", "out", "food"] as const;

/** What to do from the hostel: its nearby places on a live map, and the same places as cards under it. */
const Around = ({ slug, h, pois }: { slug: string; h: Hostel; pois: Poi[] }) => {
	const [selected, setSelected] = useState(-1);
	const mapRef = useRef<HTMLDivElement>(null);
	const markers = useMemo(() => poiMarkers(slug, selected, pois), [slug, selected, pois]);
	const hostels = useMemo(() => [hostelMarker(slug, { name: `Mad Monkey ${h.name}`, photo: h.photos[1] ?? h.photos[0] })], [slug, h]);
	const fit = useMemo(() => [hostelPoint(slug), ...pois.map((p): [number, number] => [p.lon, p.lat])], [slug, pois]);

	const toggle = (index: number, scroll: boolean) => {
		setSelected((current) => (current === index ? -1 : index));
		if (scroll) mapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
	};
	const poi = selected >= 0 ? pois[selected] : null;

	return (
		<section className={styles.around} aria-labelledby="mm-around-h">
			<div className={styles.aroundHead}>
				<div>
					<span className={shared.eyebrow}>Around the hostel</span>
					<h2 className={shared.h2} id="mm-around-h" style={{ fontSize: "clamp(26px,3vw,36px)" }}>
						What to do from Mad Monkey {h.name}
					</h2>
					<p className={shared.lede} style={{ fontSize: 16 }}>
						Picked from the places Stippl travellers save and travel writers recommend. Tap one to find it on the map, then add it to your trip.
					</p>
				</div>
				<div className={styles.legend}>
					{GROUPS.filter((g) => pois.some((p) => p.grp === g)).map((g) => (
						<span key={g}>
							<i className={styles.dot} style={{ background: POI_DOT[g] }} />
							{POI_GROUP_LABEL[g]}
						</span>
					))}
				</div>
			</div>
			<div className={styles.areaMap} ref={mapRef}>
				<LiveMap
					id={`mm-around-${slug}`}
					label={`Map of things to do around Mad Monkey ${h.name}`}
					hostels={hostels}
					pois={markers}
					poisAlways
					fit={fit}
					maxFitZoom={15}
					interactive={false}
					dots={false}
					popup={poi ? { lon: poi.lon, lat: poi.lat, children: <PoiCard poi={poi} onClose={() => setSelected(-1)} /> } : null}
					onPoiSelect={(m: PoiMarkerData) => toggle(m.index, false)}
					photoCredits={pois.flatMap((p) => p.credit ?? [])}
				/>
			</div>
			<div className={styles.pois}>
				{pois.map((p, i) => (
					<button
						type="button"
						key={p.name}
						className={cx(styles.poi, i === selected && styles.on)}
						onClick={() => toggle(i, true)}
						aria-pressed={i === selected}
					>
						{p.photo ? (
							<img src={p.photo} alt={p.name} loading="lazy" />
						) : (
							<span className={styles.tile} style={{ background: POI_FILL[p.grp] }}>
								<MarkerIcon name={p.icon} size={54} />
							</span>
						)}
						<span className={styles.pb}>
							<span className={styles.group}>
								<i className={styles.dot} style={{ background: POI_DOT[p.grp] }} />
								{POI_GROUP_LABEL[p.grp]}
								{p.label === "Must-see" ? " · Must-see" : ""}
							</span>
							<b>{p.name}</b>
							<small>{p.why}</small>
							<em>{fmtKm(p.dist)} from the hostel</em>
						</span>
					</button>
				))}
			</div>
		</section>
	);
};

interface HostelPageProps {
	slug: string;
	/** the hostel's synced list, read by the server (loader `madMonkeyHostel`); null until it exists, or another hostel's */
	initialData?: unknown;
	/** framed on Mad Monkey's own site: every link opens in a tab of its own */
	embed?: boolean;
}

/** A hostel's own page. Its nearby places come from the hostel's synced list when there is one, else from the static data. */
const HostelPage = ({ slug, initialData, embed = false }: HostelPageProps) => {
	const tab = embed ? NEW_TAB : undefined;
	const h = HOSTELS[slug];
	const pois = useMemo(() => hostelPois(slug, initialData), [slug, initialData]);
	const history = useHistory();
	const via = tripsVia(slug);
	const near = HOSTEL_SLUGS.filter((k) => k !== slug)
		.sort((x, y) => km(slug, x) - km(slug, y))
		.slice(0, 4);
	const single = useMemo(() => [hostelMarker(slug, { name: `Mad Monkey ${h.name}`, photo: h.photos[1] ?? h.photos[0] })], [slug, h]);
	const singleFit = useMemo(() => [hostelPoint(slug)], [slug]);

	const startHere = () => {
		setRoute([{ s: slug, n: defNights(slug) }]);
		history.push({ pathname: HOME_PATH, hash: "#hostels", state: { filter: h.cc, hostel: slug } });
	};
	// Framed, the route builder is a page of its own in a new tab: the button becomes a link to it, with the hostel in the address.
	const startButton = (id: string, className: string, children: React.ReactNode) =>
		embed ? (
			<a id={id} className={className} href={startPath(slug)} {...NEW_TAB}>
				{children}
			</a>
		) : (
			<button type="button" id={id} className={className} onClick={startHere}>
				{children}
			</button>
		);

	return (
		<div className={cx(shared.wrap, styles.detail)}>
			<Link className={shared.crumb} to={HOME_PATH} {...tab}>
				← All Mad Monkey hostels
			</Link>
			<div className={styles.head}>
				<div>
					<span className={shared.eyebrow}>
						<Flag cc={h.cc} /> <span style={{ verticalAlign: 3 }}>Mad Monkey · {COUNTRIES[h.cc]}</span>
					</span>
					<h1>Mad Monkey {h.name}</h1>
					<p className={cx(shared.lede)}>{h.about}</p>
					<div className={styles.facts}>
						<div className={styles.fact}>
							<b>{h.tag}</b>
							<span>in one line</span>
						</div>
						{via.length > 0 && (
							<div className={styles.fact}>
								<b>{via.length}</b>
								<span>route{via.length > 1 ? "s" : ""} stop here</span>
							</div>
						)}
					</div>
					<div className={styles.ctas}>
						{startButton(
							"mm-hostel-start",
							cx(shared.btn, shared.btnGreen),
							<>
								Start a route here <Arrow />
							</>
						)}
						<a className={cx(shared.btn, shared.btnGhost)} href={mmUrl(slug)} rel="noopener" {...tab}>
							Mad Monkey {h.name} ↗
						</a>
					</div>
				</div>
				<div className={styles.gal}>
					{h.photos.map((p, i) => (
						<img key={p} src={p} alt={`Mad Monkey ${h.name}, ${i + 1} of ${h.photos.length}`} />
					))}
				</div>
			</div>

			{pois && <Around slug={slug} h={h} pois={pois} />}

			<div className={styles.body}>
				<div className={styles.stack}>
					<div className={styles.cols}>
						<div className={styles.box}>
							<h3>What&apos;s on</h3>
							<ul className={styles.events}>
								{h.events.map((e) => {
									const [a, b] = e.split(" · ");
									return (
										<li key={e}>
											<b>{a}</b>
											{b ? ` · ${b}` : ""}
										</li>
									);
								})}
							</ul>
						</div>
						<div className={styles.box}>
							<h3>{pois ? "Good to know" : `Don't miss in ${h.name}`}</h3>
							<ul>
								{h.todo.map((e) => (
									<li key={e}>{e}</li>
								))}
							</ul>
						</div>
					</div>
					<div className={styles.box}>
						<h3>At the hostel</h3>
						<div className={styles.amen}>
							{h.amen.map((a) => (
								<span key={a} className={shared.chip}>
									{a}
								</span>
							))}
						</div>
					</div>
					{!pois && (
						<div className={styles.mapBox}>
							<LiveMap
								id={`mm-hostel-${slug}`}
								label={`Map of Mad Monkey ${h.name}`}
								hostels={single}
								fit={singleFit}
								maxFitZoom={13}
								interactive={false}
								dots={false}
							/>
						</div>
					)}
				</div>
				<aside className={cx(styles.side, embed && styles.framed)}>
					{via.length > 0 && (
						<div className={styles.box}>
							<h3>Routes that stop here</h3>
							<div className={styles.mini}>
								{via.map((t) => (
									<a key={t.id} href={routePreviewHref(t)} {...tab}>
										<img src={HOSTELS[t.stops[0][0]].photos[0]} alt="" />
										<span>
											<b>{t.name}</b>
											<small>
												{nightsOf(t)} nights · {t.stops.length} Mad Monkeys
											</small>
										</span>
									</a>
								))}
							</div>
						</div>
					)}
					<div className={cx(styles.box, styles.dark)}>
						<h3>Plan the days around it</h3>
						<p>Put Mad Monkey {h.name} on your Stippl map, add the places you want to see, and invite your travel mates.</p>
						<a className={cx(shared.btn, shared.btnGreen)} href={appUrl(`hostel_${slug.replace(/-/g, "_")}`)} {...tab}>
							Open Stippl
						</a>
					</div>
					<div className={styles.box}>
						<h3>Next Mad Monkeys from here</h3>
						<div className={styles.mini}>
							{near.map((k) => (
								<Link key={k} to={hostelPath(k)} {...tab}>
									<img src={HOSTELS[k].photos[0]} alt="" />
									<span>
										<b>
											<Flag cc={HOSTELS[k].cc} />
											{HOSTELS[k].name}
										</b>
										<small>{knownLeg(TRIPS, slug, k) || knownLeg(TRIPS, k, slug) || `about ${km(slug, k)} km away`}</small>
									</span>
								</Link>
							))}
						</div>
						{startButton("mm-hostel-build", cx(shared.btn, shared.btnGhost, styles.small), "Build a route from here")}
					</div>
				</aside>
			</div>
		</div>
	);
};

export default HostelPage;
